// ---------------------------------------------------------------------------
// Constants for sysml-mfe
// ---------------------------------------------------------------------------

/** Compare controllers by generation counter instead of object identity.
 *  Use with `@property({ attribute: false, hasChanged: generationChanged })` */
export function generationChanged(
  newVal: { generation: number } | undefined,
  oldVal: { generation: number } | undefined,
): boolean {
  if (!newVal || !oldVal) return newVal !== oldVal;
  return newVal.generation !== oldVal.generation;
}

/** Debounce delay for source text → parse pipeline (ms) */
export const PARSE_DEBOUNCE_MS = 300;

/** Default diagram node dimensions (card-style nodes) */
export const DEFAULT_NODE_WIDTH = 200;
export const DEFAULT_NODE_HEIGHT = 90;

/** Default gap between auto-laid-out nodes */
export const GAP_X = 40;
export const GAP_Y = 30;

/** Default editor/diagram split ratio (percentage for editor) */
export const DEFAULT_EDITOR_WIDTH = 50;

/** Min/max split constraints */
export const MIN_EDITOR_WIDTH = 20;
export const MAX_EDITOR_WIDTH = 80;

/** Explorer sidebar size constraints (px) */
export const EXPLORER_MIN_WIDTH = 150;
export const EXPLORER_MAX_WIDTH = 400;
export const DEFAULT_EXPLORER_WIDTH = 200;

/** Right panel size constraints (px) */
export const RIGHT_PANEL_MIN_WIDTH = 250;
export const RIGHT_PANEL_MAX_WIDTH = 600;
export const DEFAULT_RIGHT_PANEL_WIDTH = 320;

/** Bottom panel size constraints (px) */
export const BOTTOM_PANEL_MIN_HEIGHT = 80;
export const BOTTOM_PANEL_MAX_HEIGHT = 400;
export const DEFAULT_BOTTOM_PANEL_HEIGHT = 150;

/** Debounce delay for layout persistence saves (ms) */
export const LAYOUT_SAVE_DEBOUNCE_MS = 300;

/** Auto-save delay (ms) — 0 = disabled */
export const AUTO_SAVE_DELAY_MS = 0;

/** localStorage keys */
export const STORAGE_KEYS = {
  DOCUMENTS: 'sysml:documents',
  FOLDERS: 'sysml:folders',
  CHAT_HISTORY: 'sysml:chat-history',
  PREFERENCES: 'sysml:preferences',
  LAYOUT: 'sysml:layout',
  THEME: 'sysml:theme',
  INGESTION_API_KEYS: 'sysml:ingestion-api-keys',
  INGESTION_HISTORY: 'sysml:ingestion-history',
  INGESTION_JOURNALS: 'sysml:ingestion-journals',
} as const;

/** Right panel tab identifiers */
export const RIGHT_PANEL_TABS = [
  'chatbot',
  'health',
  'verification',
  'requirements',
  'ingest',
  'builder',
  'solver',
  'quickRef',
  'history',
] as const;

export type RightPanelTab = (typeof RIGHT_PANEL_TABS)[number];

/** Serialized layout state for localStorage persistence */
export interface LayoutState {
  explorerWidth: number;
  explorerCollapsed: boolean;
  editorSplitPct: number;
  rightPanelWidth: number;
  rightPanelOpen: boolean;
  rightPanelTab: RightPanelTab;
  bottomPanelHeight: number;
  showSearch: boolean;
  showDiagnostics: boolean;
}

// ---------------------------------------------------------------------------
// V2 Layout — VS Code-style zone model
// ---------------------------------------------------------------------------

/** Activity bar items (left icon rail) */
export type ActivityBarItem = 'explorer' | 'search' | 'model-tree';

export const ACTIVITY_BAR_ITEMS: ReadonlyArray<{ id: ActivityBarItem; icon: string; label: string }> = [
  { id: 'explorer', icon: 'layout-dashboard', label: 'Explorer' },
  { id: 'search', icon: 'search', label: 'Search' },
  { id: 'model-tree', icon: 'git-branch', label: 'Model Tree' },
];

/** Bottom panel tab identifiers */
export type BottomPanelTab = 'diagnostics' | 'search' | 'solver' | 'history' | 'simulation';

export const BOTTOM_PANEL_TABS: ReadonlyArray<{ id: BottomPanelTab; icon: string; label: string }> = [
  { id: 'diagnostics', icon: 'alert-triangle', label: 'Diagnostics' },
  { id: 'search', icon: 'search', label: 'Search' },
  { id: 'solver', icon: 'sliders-horizontal', label: 'Solver' },
  { id: 'history', icon: 'clock', label: 'History' },
  { id: 'simulation', icon: 'play', label: 'Simulation' },
];

/** Secondary sidebar tab identifiers */
export type SecondarySidebarTab = 'chatbot' | 'health' | 'verification' | 'requirements' | 'ingest' | 'builder' | 'quickRef';

export const SECONDARY_SIDEBAR_TABS: ReadonlyArray<{ id: SecondarySidebarTab; icon: string; label: string }> = [
  { id: 'chatbot', icon: 'sparkles', label: 'AI Chat' },
  { id: 'health', icon: 'activity', label: 'Model Health' },
  { id: 'verification', icon: 'list-checks', label: 'Verification' },
  { id: 'requirements', icon: 'clipboard-list', label: 'Requirements' },
  { id: 'ingest', icon: 'inbox', label: 'Ingest' },
  { id: 'builder', icon: 'construction', label: 'Builder' },
  { id: 'quickRef', icon: 'book-open', label: 'Quick Ref' },
];

/** Activity bar width (px) */
export const ACTIVITY_BAR_WIDTH = 48;

/** Status bar height (px) */
export const STATUS_BAR_HEIGHT = 24;

/** Secondary sidebar size constraints (px) */
export const SECONDARY_SIDEBAR_MIN_WIDTH = 250;
export const SECONDARY_SIDEBAR_MAX_WIDTH = 600;
export const DEFAULT_SECONDARY_SIDEBAR_WIDTH = 320;

/** V2 layout state — full zone model */
export interface LayoutStateV2 {
  version: 2;
  primarySidebarWidth: number;
  primarySidebarCollapsed: boolean;
  primarySidebarActiveItem: ActivityBarItem;
  editorSplitPct: number;
  bottomPanelHeight: number;
  bottomPanelCollapsed: boolean;
  bottomPanelActiveTab: BottomPanelTab;
  secondarySidebarWidth: number;
  secondarySidebarCollapsed: boolean;
  secondarySidebarActiveTab: SecondarySidebarTab;
  diagramView: 'diagram' | 'ibd' | 'sequence' | 'statemachine' | 'activity' | 'package' | 'requirement' | 'usecase' | 'parametric';
  focusMode: 'none' | 'editor' | 'diagram';
}

/** Layout presets — named partial LayoutStateV2 configs */
export type LayoutPresetName = 'editing' | 'modeling' | 'review' | 'verification' | 'presentation';

export const LAYOUT_PRESETS: Record<LayoutPresetName, Partial<LayoutStateV2>> = {
  editing: {
    primarySidebarCollapsed: false,
    primarySidebarActiveItem: 'explorer',
    editorSplitPct: 70,
    bottomPanelCollapsed: true,
    secondarySidebarCollapsed: true,
    focusMode: 'none',
  },
  modeling: {
    primarySidebarCollapsed: false,
    primarySidebarActiveItem: 'model-tree',
    editorSplitPct: 50,
    bottomPanelCollapsed: false,
    bottomPanelActiveTab: 'diagnostics',
    secondarySidebarCollapsed: true,
    focusMode: 'none',
  },
  review: {
    primarySidebarCollapsed: true,
    editorSplitPct: 50,
    bottomPanelCollapsed: false,
    bottomPanelActiveTab: 'diagnostics',
    secondarySidebarCollapsed: false,
    secondarySidebarActiveTab: 'health',
    focusMode: 'none',
  },
  verification: {
    primarySidebarCollapsed: true,
    editorSplitPct: 40,
    bottomPanelCollapsed: false,
    bottomPanelActiveTab: 'diagnostics',
    secondarySidebarCollapsed: false,
    secondarySidebarActiveTab: 'verification',
    focusMode: 'none',
  },
  presentation: {
    primarySidebarCollapsed: true,
    editorSplitPct: 0,
    bottomPanelCollapsed: true,
    secondarySidebarCollapsed: true,
    focusMode: 'diagram',
  },
};

/** Verification method options */
export const VERIFICATION_METHODS = [
  { value: 'test', label: 'Test' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'demonstration', label: 'Demonstration' },
] as const;

/** Verification status options */
export const VERIFICATION_STATUSES = [
  { value: 'planned', label: 'Planned', color: 'var(--r5-text-muted)' },
  { value: 'passed', label: 'Passed', color: 'var(--r5-success-700)' },
  { value: 'failed', label: 'Failed', color: 'var(--r5-destructive-700)' },
  { value: 'blocked', label: 'Blocked', color: 'var(--r5-warning-700)' },
] as const;

/** Requirement priority options */
export const REQUIREMENT_PRIORITIES = [
  { value: 'high', label: 'High', color: 'var(--r5-destructive-700)' },
  { value: 'medium', label: 'Medium', color: 'var(--r5-warning-700)' },
  { value: 'low', label: 'Low', color: 'var(--r5-text-muted)' },
] as const;

/** Builder element categories */
export const BUILDER_CATEGORIES = [
  { id: 'structure', label: 'Structure', icon: 'box' },
  { id: 'behavior', label: 'Behavior', icon: 'activity' },
  { id: 'requirements', label: 'Requirements', icon: 'clipboard-list' },
  { id: 'constraints', label: 'Constraints', icon: 'shield' },
  { id: 'views', label: 'Views', icon: 'eye' },
] as const;

/** Full toaster SysML v2 model — deep decomposition for IBD drill-down demo */
export const TOASTER_SYSML_DOCUMENT = `package ToasterModel {

  // ══════════════════════════════════════════════════════════════════════
  // Port Definitions — interfaces between subsystems
  // ══════════════════════════════════════════════════════════════════════

  port def ElectricalPort {
    attribute voltage : Real;
    attribute currentRating : Real;
  }

  port def ThermalPort {
    attribute maxTemp : Real;
  }

  port def MechanicalPort {
    attribute forceRating : Real;
  }

  port def SignalPort {
    attribute signalType : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Level 0 — Top-level Toaster
  // ══════════════════════════════════════════════════════════════════════

  part def Toaster {
    doc /* Pop-up toaster — 120VAC, 850W, 2-slot, mechanical timer. */;
    attribute ratedPower : Real;
    attribute ratedVoltage : Real;
    attribute slotCount : Integer;
    port powerIn : ElectricalPort;
    part chassis : ChassisAssembly;
    part heatingSystem : HeatingAssembly;
    part carriage : CarriageMechanism;
    part controls : ControlSystem;
    part power : PowerSystem;

    connection : ACPowerConnection connect power.powerOut to heatingSystem.powerIn;
    connection : ACPowerConnection connect power.powerOut to controls.powerIn;
    connection : MechanicalLinkage connect controls.latchRelease to carriage.latchInterface;
    connection : ACPowerConnection connect controls.browningInput to heatingSystem.powerIn;
    connection : HeatFlowConnection connect heatingSystem.heatOut to chassis.thermalOut;

    exhibit state ToasterStates;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Level 1 — Chassis Assembly
  // ══════════════════════════════════════════════════════════════════════

  part def ChassisAssembly {
    doc /* Structural enclosure: outer shell, base, and bread chamber. */;
    port thermalOut : ThermalPort;
    part shell : OuterShell;
    part base : BaseAssembly;
    part chamber : BreadChamber;
  }

  // Level 2 — Outer Shell
  part def OuterShell {
    doc /* Brushed stainless steel exterior with ventilation. */;
    part shellBody : ShellBody;
    part endCaps : EndCapAssembly;
  }

  // Level 3 — Shell Body
  part def ShellBody {
    doc /* Two-piece stamped 304 stainless steel shell, 0.6mm. */;
    attribute material : String;
    attribute thickness : Real;
    part leftPanel : StainlessPanel;
    part rightPanel : StainlessPanel;
    part fasteners : ScrewSet;
  }

  // Level 4 — Leaf parts
  part def StainlessPanel {
    doc /* Stamped 304 SS panel with ventilation louver pattern. */;
    attribute material : String;
    attribute gauge : Real;
    attribute louverCount : Integer;
  }

  part def ScrewSet {
    doc /* #6-32 stainless machine screws with split lock washers. */;
    attribute screwSize : String;
    attribute quantity : Integer;
  }

  // Level 3 — End Caps
  part def EndCapAssembly {
    doc /* Injection-molded Bakelite phenolic resin end caps. */;
    part frontCap : BakeliteCap;
    part rearCap : BakeliteCap;
    part inserts : BrassInsertSet;
  }

  part def BakeliteCap {
    doc /* Phenolic resin molding — thermal insulator, UL 94 V-0. */;
    attribute material : String;
    attribute maxServiceTemp : Real;
  }

  part def BrassInsertSet {
    doc /* M3 brass threaded inserts molded into Bakelite caps. */;
    attribute threadSize : String;
    attribute quantity : Integer;
  }

  // Level 2 — Base Assembly
  part def BaseAssembly {
    doc /* Zinc-plated cold-rolled steel base with crumb tray. */;
    part basePlate : BasePlate;
    part feet : RubberFeetSet;
    part crumbTray : CrumbTray;
  }

  part def BasePlate {
    doc /* 1.0mm cold-rolled steel, zinc-plated structural base. */;
    attribute material : String;
    attribute thickness : Real;
    part mountingBosses : MountingBossSet;
  }

  part def MountingBossSet {
    doc /* Spot-welded steel standoffs, M4 threaded, qty 6. */;
    attribute quantity : Integer;
    attribute threadSize : String;
  }

  part def RubberFeetSet {
    doc /* Silicone rubber pads, Shore A 60, anti-slip and thermal isolation. */;
    attribute material : String;
    attribute durometer : Integer;
    attribute quantity : Integer;
  }

  part def CrumbTray {
    doc /* Slide-out stainless steel crumb collection tray. */;
    part trayBody : StainlessTrayBody;
    part trayHandle : TrayHandle;
    part slides : SlideRailSet;
  }

  part def StainlessTrayBody {
    doc /* 0.5mm stamped stainless with 3mm turned-up edges. */;
    attribute material : String;
    attribute lipHeight : Real;
  }

  part def TrayHandle {
    doc /* Spot-welded finger tab with Bakelite grip cover. */;
    attribute gripMaterial : String;
  }

  part def SlideRailSet {
    doc /* Rolled stainless channel rails, press-fit to base. */;
    attribute quantity : Integer;
  }

  // Level 2 — Bread Chamber
  part def BreadChamber {
    doc /* Dual-slot bread chamber with IR-reflective walls. */;
    port heatIn : ThermalPort;
    part walls : ChamberWallSet;
    part frame : ChamberFrame;
    part guides : BreadGuideSet;
  }

  part def ChamberWallSet {
    doc /* 0.5mm aluminized steel panels — >90% IR reflectivity. */;
    part innerPanels : AluminizedPanel;
    part guardWires : ElementGuardWire;
  }

  part def AluminizedPanel {
    doc /* Polished aluminized steel for infrared reflection. */;
    attribute material : String;
    attribute reflectivity : Real;
  }

  part def ElementGuardWire {
    doc /* 1.2mm nickel-plated steel wire — prevents bread-element contact. */;
    attribute wireDiameter : Real;
    attribute material : String;
  }

  part def ChamberFrame {
    doc /* 1.0mm galvanized steel structural skeleton. */;
    part topRail : SteelChannel;
    part bottomRail : SteelBar;
    part struts : VerticalStrutSet;
  }

  part def SteelChannel {
    doc /* U-channel steel rail forming bread slot opening. */;
    attribute profile : String;
  }

  part def SteelBar {
    doc /* Flat steel bar supporting element frames. */;
    attribute width : Real;
  }

  part def VerticalStrutSet {
    doc /* Steel angle brackets connecting top and bottom rails. */;
    attribute quantity : Integer;
  }

  part def BreadGuideSet {
    doc /* Spring-loaded wire guides centering bread in slot. */;
    part guideFrame : GuideWireFrame;
    part tensionSprings : GuideTensionSpring;
  }

  part def GuideWireFrame {
    doc /* 2.0mm nickel-plated steel wire bent to centering profile. */;
    attribute wireDiameter : Real;
  }

  part def GuideTensionSpring {
    doc /* Extension springs, 0.5mm music wire. */;
    attribute wireDiameter : Real;
    attribute material : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Level 1 — Heating Assembly
  // ══════════════════════════════════════════════════════════════════════

  part def HeatingAssembly {
    doc /* Nichrome wire heating elements on mica insulation sheets. */;
    port powerIn : ElectricalPort;
    port heatOut : ThermalPort;
    part elements : HeatingElementBank;
    part wiring : ElementWiringHarness;
    part thermalMgmt : ThermalManagement;
  }

  // Level 2 — Heating Element Bank
  part def HeatingElementBank {
    doc /* Four heating elements: 2 outer + 2 inner/shared. */;
    port powerIn : ElectricalPort;
    part element : HeatingElement;
  }

  // Level 3 — Individual Heating Element
  part def HeatingElement {
    doc /* Single nichrome-on-mica heating element, ~24 ohms. */;
    attribute resistance : Real;
    part wire : NichromeWireElement;
    part insulation : MicaInsulationSheet;
    part frame : ElementFrame;
  }

  // Level 4 — Nichrome Wire
  part def NichromeWireElement {
    doc /* Nichrome 80/20 (80% Ni, 20% Cr), 26 AWG, zigzag pattern. */;
    attribute alloy : String;
    attribute gaugeAWG : Integer;
    attribute resistivity : Real;
    part resistanceWire : NichromeWire;
    part crimps : CrimpConnectorSet;
  }

  // Level 5 — Leaf parts
  part def NichromeWire {
    doc /* Ni80/Cr20 resistance wire, 1100 deg C max surface temp. */;
    attribute composition : String;
    attribute meltingPoint : Real;
  }

  part def CrimpConnectorSet {
    doc /* Nickel-plated brass crimp connectors at wire terminations. */;
    attribute material : String;
    attribute quantity : Integer;
  }

  // Level 4 — Mica Insulation
  part def MicaInsulationSheet {
    doc /* Natural muscovite mica, 0.3mm, 500 deg C continuous. */;
    attribute material : String;
    attribute thickness : Real;
    attribute dielectricStrength : Real;
    part micaPanel : MicaPanel;
    part retentionSlots : WireRetentionSlot;
  }

  part def MicaPanel {
    doc /* Precision-cut muscovite mica dielectric insulator. */;
    attribute maxContinuousTemp : Real;
  }

  part def WireRetentionSlot {
    doc /* Laser-cut slots holding nichrome wire in zigzag pattern. */;
    attribute slotSpacing : Real;
  }

  // Level 4 — Element Frame
  part def ElementFrame {
    doc /* Stainless steel frame mounting element to chamber. */;
    part rails : FrameRail;
    part endBrackets : FrameEndBracket;
    part terminals : TerminalPostSet;
  }

  part def FrameRail {
    doc /* 0.8mm SS channel rails holding mica sheet vertically. */;
    attribute material : String;
    attribute thickness : Real;
  }

  part def FrameEndBracket {
    doc /* Stamped stainless brackets for chamber frame mounting. */;
    attribute material : String;
  }

  part def TerminalPostSet {
    doc /* M3 brass studs with ceramic insulation bushings. */;
    attribute threadSize : String;
    attribute insulationMaterial : String;
  }

  // Level 2 — Wiring Harness
  part def ElementWiringHarness {
    doc /* Fiberglass-insulated nickel-plated copper wiring, 450 deg C rated. */;
    port powerIn : ElectricalPort;
    part interconnects : InterconnectWireSet;
    part terminals : WireTerminalSet;
    part standoffs : CeramicStandoffSet;
  }

  part def InterconnectWireSet {
    doc /* 16 AWG fiberglass-insulated wire — element jumpers and leads. */;
    attribute gaugeAWG : Integer;
    attribute insulationType : String;
    attribute tempRating : Real;
  }

  part def WireTerminalSet {
    doc /* High-temp brass ring terminals with nickel plating. */;
    attribute material : String;
  }

  part def CeramicStandoffSet {
    doc /* Steatite ceramic insulators preventing wire-chassis contact. */;
    attribute material : String;
    attribute quantity : Integer;
    attribute tempRating : Real;
  }

  // Level 2 — Thermal Management
  part def ThermalManagement {
    doc /* Heat shields and thermal fuse for safe operation. */;
    part heatShields : HeatShieldSet;
    part thermalFuse : ThermalFuse;
  }

  part def HeatShieldSet {
    doc /* 0.3mm aluminized steel reflectors between elements and shell. */;
    part panels : ShieldPanel;
    part mountingTabs : ShieldMountingTab;
  }

  part def ShieldPanel {
    doc /* Formed aluminized steel following shell contour, 8mm air gap. */;
    attribute material : String;
    attribute airGap : Real;
  }

  part def ShieldMountingTab {
    doc /* Bent tabs riveted to chamber frame. */;
    attribute attachmentMethod : String;
  }

  part def ThermalFuse {
    doc /* One-shot ceramic-body fuse, 240 deg C, Microtemp type, non-resettable. */;
    attribute tripTemp : Real;
    attribute resettable : String;
    part fuseBody : CeramicFuseBody;
    part fuseLeads : FuseLeadSet;
  }

  part def CeramicFuseBody {
    doc /* Ceramic tube with fusible alloy link. */;
    attribute bodyMaterial : String;
  }

  part def FuseLeadSet {
    doc /* Nickel wire leads crimped to harness connectors. */;
    attribute material : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Level 1 — Carriage Mechanism
  // ══════════════════════════════════════════════════════════════════════

  part def CarriageMechanism {
    doc /* Spring-loaded bread carriage with lever, latch, and pop-up. */;
    port leverInput : MechanicalPort;
    port latchInterface : MechanicalPort;
    part carriageFrame : CarriageFrame;
    part lever : LeverAssembly;
    part spring : SpringAssembly;
    part latch : LatchMechanism;
  }

  // Level 2 — Carriage Frame
  part def CarriageFrame {
    doc /* Stamped galvanized steel U-shaped cradle. */;
    part sideRails : CarriageSideRail;
    part crossBars : CarriageCrossBar;
    part breadRack : BreadSupportRack;
  }

  part def CarriageSideRail {
    doc /* Steel channels running full slot length with guide tabs. */;
    attribute material : String;
  }

  part def CarriageCrossBar {
    doc /* Steel rods connecting side rails — bread rests on these. */;
    attribute quantity : Integer;
  }

  part def BreadSupportRack {
    doc /* Nickel-plated steel wire grid, spot-welded to frame. */;
    attribute material : String;
  }

  // Level 2 — Lever Assembly
  part def LeverAssembly {
    doc /* User-operated lever converting push-down to carriage descent. */;
    port leverInput : MechanicalPort;
    part handle : LeverHandle;
    part arm : LeverArm;
    part linkage : LeverLinkage;
  }

  // Level 3 — Lever Handle
  part def LeverHandle {
    doc /* Bakelite molded handle — thermally insulating ergonomic grip. */;
    part handleBody : BakeliteHandleBody;
    part steelInsert : HandleSteelInsert;
  }

  part def BakeliteHandleBody {
    doc /* Phenolic resin handle, 150 deg C service temp. */;
    attribute material : String;
  }

  part def HandleSteelInsert {
    doc /* Stainless threaded insert molded into handle. */;
    attribute material : String;
  }

  // Level 3 — Lever Arm
  part def LeverArm {
    doc /* 1.5mm stainless flat bar pivoting at fulcrum. */;
    attribute material : String;
    attribute thickness : Real;
    part shaft : LeverShaft;
    part pivot : PivotAssembly;
  }

  // Level 4 — Pivot Assembly
  part def LeverShaft {
    doc /* Stainless bar running through end cap slot. */;
    attribute material : String;
  }

  part def PivotAssembly {
    doc /* Hardened steel pin in oil-impregnated bronze bushing. */;
    part pivotPin : HardenedSteelPin;
    part bushing : BronzeBushing;
  }

  // Level 5 — Pivot leaf parts
  part def HardenedSteelPin {
    doc /* 3mm hardened steel dowel pin, press-fit. */;
    attribute diameter : Real;
    attribute hardness : String;
  }

  part def BronzeBushing {
    doc /* Oil-impregnated bronze — self-lubricating, wear resistant. */;
    attribute material : String;
    attribute lubricationType : String;
  }

  // Level 3 — Linkage
  part def LeverLinkage {
    doc /* Connecting rod with clevis joints linking lever to carriage. */;
    part connectingRod : ConnectingRod;
    part clevisPins : ClevisPinSet;
    part retainers : EClipSet;
  }

  part def ConnectingRod {
    doc /* 1.2mm steel wire link — lever arm to carriage frame. */;
    attribute wireDiameter : Real;
  }

  part def ClevisPinSet {
    doc /* 2mm steel pins allowing articulation at each joint. */;
    attribute pinDiameter : Real;
    attribute quantity : Integer;
  }

  part def EClipSet {
    doc /* Spring steel E-clips retaining clevis pins. */;
    attribute material : String;
    attribute quantity : Integer;
  }

  // Level 2 — Spring Assembly
  part def SpringAssembly {
    doc /* Compression spring providing pop-up force for toast ejection. */;
    port springForce : MechanicalPort;
    part mainSpring : CompressionSpring;
    part guide : SpringGuide;
  }

  // Level 3 — Compression Spring
  part def CompressionSpring {
    doc /* 1.2mm music wire (ASTM A228), 25mm OD, 15 N/mm rate. */;
    attribute wireDiameter : Real;
    attribute outerDiameter : Real;
    attribute freeLength : Real;
    attribute springRate : Real;
    attribute material : String;
  }

  // Level 3 — Spring Guide
  part def SpringGuide {
    doc /* Polished stainless rod preventing spring buckling. */;
    part guideRod : PolishedRod;
    part upperCup : RetainerCup;
    part lowerCup : RetainerCup;
  }

  // Level 4
  part def PolishedRod {
    doc /* 6mm polished stainless steel guide shaft. */;
    attribute diameter : Real;
    attribute material : String;
  }

  part def RetainerCup {
    doc /* Stamped steel washer for spring seating. */;
    attribute material : String;
    attribute attachmentMethod : String;
  }

  // Level 2 — Latch Mechanism
  part def LatchMechanism {
    doc /* Holds carriage down during toasting; released by bimetal timer. */;
    port releaseSignal : MechanicalPort;
    part hook : LatchHook;
    part keeper : KeeperPlate;
  }

  // Level 3 — Latch Hook
  part def LatchHook {
    doc /* Hardened spring steel hook engaging keeper, Rc 45-50. */;
    part hookBody : SpringSteelHook;
    part hookPivot : HookPivotScrew;
    part returnSpring : TorsionSpring;
  }

  part def SpringSteelHook {
    doc /* 1.5mm spring steel, heat-treated to Rockwell C 45-50. */;
    attribute material : String;
    attribute hardness : String;
  }

  part def HookPivotScrew {
    doc /* 2mm steel shoulder screw mounted in carriage frame. */;
    attribute diameter : Real;
  }

  part def TorsionSpring {
    doc /* 0.4mm music wire torsion spring biasing hook to engaged. */;
    attribute wireDiameter : Real;
    attribute material : String;
  }

  // Level 3 — Keeper Plate
  part def KeeperPlate {
    doc /* Hardened steel catch plate mounted on chassis. */;
    part keeperFace : PolishedKeeperFace;
    part mountingBracket : KeeperBracket;
  }

  part def PolishedKeeperFace {
    doc /* Polished engagement surface for latch hook. */;
    attribute surfaceFinish : String;
  }

  part def KeeperBracket {
    doc /* Steel L-bracket riveted to base plate. */;
    attribute material : String;
    attribute attachmentMethod : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Level 1 — Control System
  // ══════════════════════════════════════════════════════════════════════

  part def ControlSystem {
    doc /* Browning control, bimetallic timer, and safety interlock. */;
    port powerIn : ElectricalPort;
    port browningInput : SignalPort;
    port latchRelease : MechanicalPort;
    part browning : BrowningControl;
    part timer : TimerCircuit;
    part interlock : SafetyInterlock;
  }

  // Level 2 — Browning Control
  part def BrowningControl {
    doc /* User-adjustable browning level 1-7 via rotary dial. */;
    part dial : BrowningDial;
    part potentiometer : Potentiometer;
  }

  // Level 3 — Browning Dial
  part def BrowningDial {
    doc /* Bakelite rotary knob with detent positions. */;
    part knob : DialKnob;
    part shaft : DialShaft;
    part detentSpring : DetentLeafSpring;
    part detentBall : DetentBall;
  }

  part def DialKnob {
    doc /* Phenolic resin with numbered markings 1-7. */;
    attribute material : String;
    attribute positions : Integer;
  }

  part def DialShaft {
    doc /* 4mm brass rod pressed into knob. */;
    attribute diameter : Real;
    attribute material : String;
  }

  part def DetentLeafSpring {
    doc /* Leaf spring providing tactile click at each position. */;
    attribute material : String;
  }

  part def DetentBall {
    doc /* 2mm hardened steel ball bearing for detent mechanism. */;
    attribute diameter : Real;
  }

  // Level 3 — Potentiometer
  part def Potentiometer {
    doc /* Wire-wound 500 ohm potentiometer on dial shaft. */;
    attribute resistance : Real;
    part resistanceElement : PotResistanceElement;
    part wiper : WiperContact;
    part housing : PotHousing;
  }

  part def PotResistanceElement {
    doc /* Nichrome wire wound on ceramic card. */;
    attribute material : String;
  }

  part def WiperContact {
    doc /* Beryllium copper spring wiper contact. */;
    attribute material : String;
  }

  part def PotHousing {
    doc /* Bakelite body with solder lug terminals. */;
    attribute material : String;
  }

  // Level 2 — Timer Circuit (Bimetallic type)
  part def TimerCircuit {
    doc /* Bimetallic strip mechanical timer — deflects to release latch. */;
    part bimetalStrip : BimetallicStripAssembly;
  }

  // Level 3 — Bimetallic Strip Assembly
  part def BimetallicStripAssembly {
    doc /* Invar/brass laminated strip, 40x6x0.8mm. */;
    attribute length : Real;
    attribute width : Real;
    part strip : BimetallicStrip;
    part calibrationScrew : CalibrationScrew;
    part mountingBracket : StripMountBracket;
    part auxHeater : AuxiliaryHeaterWire;
    part tripLever : TripLever;
  }

  part def BimetallicStrip {
    doc /* Invar/brass laminate — differential thermal expansion drives deflection. */;
    attribute materials : String;
    attribute thickness : Real;
  }

  part def CalibrationScrew {
    doc /* M2 brass screw setting strip pre-load, factory calibrated. */;
    attribute threadSize : String;
    attribute material : String;
  }

  part def StripMountBracket {
    doc /* Stainless bracket with ceramic insulation pad. */;
    attribute material : String;
  }

  part def AuxiliaryHeaterWire {
    doc /* 32 AWG nichrome wire wrapped around strip — current set by pot. */;
    attribute gaugeAWG : Integer;
    attribute material : String;
  }

  part def TripLever {
    doc /* Steel finger on strip end — pushes latch hook at release threshold. */;
    attribute material : String;
  }

  // Level 2 — Safety Interlock
  part def SafetyInterlock {
    doc /* Lever microswitch — elements only energize when fully latched. */;
    part leverSwitch : LeverMicroswitch;
  }

  part def LeverMicroswitch {
    doc /* SPST snap-action switch, 15A at 125VAC, silver-cadmium oxide contacts. */;
    attribute currentRating : Real;
    attribute voltageRating : Real;
    part switchBody : MicroswitchBody;
    part actuatorLever : SwitchActuator;
    part mountingBracket : SwitchBracket;
  }

  part def MicroswitchBody {
    doc /* Phenolic housing with Ag-CdO contacts. */;
    attribute contactMaterial : String;
  }

  part def SwitchActuator {
    doc /* Spring steel leaf with roller tip. */;
    attribute material : String;
  }

  part def SwitchBracket {
    doc /* Steel bracket riveted to chassis. */;
    attribute material : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Level 1 — Power System
  // ══════════════════════════════════════════════════════════════════════

  part def PowerSystem {
    doc /* 120VAC power entry, distribution, and chassis ground. */;
    port powerIn : ElectricalPort;
    port powerOut : ElectricalPort;
    port chassisGround : ElectricalPort;
    part cord : PowerCordAssembly;
    part distribution : PowerDistribution;
    part ground : ChassisGroundConnection;
  }

  // Level 2 — Power Cord
  part def PowerCordAssembly {
    doc /* 16 AWG SPT-2, 3-conductor, NEMA 5-15P plug, 0.9m, UL listed. */;
    port plugEnd : ElectricalPort;
    part lineCord : LineCord;
    part plug : PowerPlug;
    part strainRelief : StrainRelief;
  }

  // Level 3 — Line Cord
  part def LineCord {
    doc /* 16 AWG SPT-2 type, PVC jacket, UL listed. */;
    attribute length : Real;
    part hotConductor : CopperConductor;
    part neutralConductor : CopperConductor;
    part groundConductor : CopperConductor;
    part outerJacket : PVCJacket;
  }

  // Level 4
  part def CopperConductor {
    doc /* Stranded copper conductor with color-coded insulation. */;
    attribute gaugeAWG : Integer;
    attribute insulationColor : String;
    attribute material : String;
  }

  part def PVCJacket {
    doc /* Flame-retardant PVC outer sheath. */;
    attribute material : String;
    attribute flameRating : String;
  }

  // Level 3 — Plug
  part def PowerPlug {
    doc /* NEMA 5-15P 3-prong grounded plug, molded PVC body. */;
    part hotBlade : PlugBlade;
    part neutralBlade : PlugBlade;
    part groundPin : PlugGroundPin;
    part plugBody : MoldedPlugBody;
  }

  // Level 4
  part def PlugBlade {
    doc /* Brass blade contact — narrow (hot) or wide (neutral, polarized). */;
    attribute material : String;
    attribute width : String;
  }

  part def PlugGroundPin {
    doc /* Brass round ground pin. */;
    attribute material : String;
  }

  part def MoldedPlugBody {
    doc /* Molded PVC with integral strain relief. */;
    attribute material : String;
  }

  // Level 3 — Strain Relief
  part def StrainRelief {
    doc /* Prevents wire pullout and abrasion at chassis entry. */;
    part grommet : NeopreneGrommet;
    part cordClamp : CordClamp;
  }

  part def NeopreneGrommet {
    doc /* Neoprene rubber, compression fit in base plate. */;
    attribute material : String;
  }

  part def CordClamp {
    doc /* Stamped steel saddle clamp with M3 screw. */;
    attribute material : String;
    attribute screwSize : String;
  }

  // Level 2 — Power Distribution
  part def PowerDistribution {
    doc /* Terminal block and internal bus wiring. */;
    part terminalBlock : TerminalBlock;
    part internalWiring : InternalWiringSet;
  }

  part def TerminalBlock {
    doc /* Steatite ceramic 4-position screw terminal block. */;
    attribute material : String;
    attribute positions : Integer;
    part body : CeramicTerminalBody;
    part screwTerminals : ScrewTerminalSet;
  }

  part def CeramicTerminalBody {
    doc /* Steatite ceramic block — 1000 deg C rated. */;
    attribute tempRating : Real;
  }

  part def ScrewTerminalSet {
    doc /* M3 brass screws with square clamp plates. */;
    attribute quantity : Integer;
    attribute material : String;
  }

  part def InternalWiringSet {
    doc /* 14 AWG stranded copper with fiberglass insulation. */;
    attribute gaugeAWG : Integer;
    attribute insulationType : String;
  }

  // Level 2 — Chassis Ground
  part def ChassisGroundConnection {
    doc /* Protective earth ground bonded to chassis. */;
    part groundLug : GroundLug;
    part groundStud : GroundStud;
    part starWasher : StarWasher;
  }

  part def GroundLug {
    doc /* Brass ring terminal, #10 stud size. */;
    attribute material : String;
  }

  part def GroundStud {
    doc /* M5 stainless stud welded to base plate. */;
    attribute material : String;
    attribute size : String;
  }

  part def StarWasher {
    doc /* Internal tooth lock washer — ensures positive ground contact. */;
    attribute type : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // State Definitions
  // ══════════════════════════════════════════════════════════════════════

  state def ToasterStates {
    doc /* Operational states of the toaster lifecycle. */;
    state off {
      doc /* Idle — no power to elements, carriage up. */;
    }
    state loading {
      entry ToastCycle.pressLever;
      do ToastCycle.engageLatch;
    }
    state heating {
      entry ToastCycle.energizeElements;
      do ToastCycle.heatBread;
      exit SafetyShutdown.deEnergizeElements;
    }
    state releasing {
      entry ToastCycle.releaseLatch;
    }
    state popping {
      do ToastCycle.ejectToast;
    }
    state fault {
      entry SafetyShutdown.tripThermalFuse;
      do SafetyShutdown.alertUser;
    }
    transition first off then loading when [leverPressed];
    transition first loading then heating when [latchEngaged];
    transition first heating then releasing when [bimetalDeflection >= tripThreshold];
    transition first releasing then popping when [latchReleased];
    transition first popping then off when [carriageUp];
    transition first heating then fault when [internalTemp > 240];
  }

  // ══════════════════════════════════════════════════════════════════════
  // Requirement Definitions
  // ══════════════════════════════════════════════════════════════════════

  requirement def ThermalSafetyReq {
    doc /* Thermal fuse shall open the circuit if temp exceeds 240 deg C. SAF-001. */;
    attribute maxInternalTemp : Real;
  }

  requirement def InterlockReq {
    doc /* Elements shall only energize when lever microswitch is closed. SAF-002. */;
  }

  requirement def SurfaceTempReq {
    doc /* Accessible surfaces shall not exceed 65 deg C per UL 1026. SAF-003. */;
    attribute maxSurfaceTemp : Real;
  }

  requirement def GroundReq {
    doc /* Chassis shall connect to protective earth ground. SAF-004. */;
  }

  requirement def ToastTimeReq {
    doc /* Medium toast (setting 4) in 150 +/- 15 seconds from room temp. PER-001. */;
    attribute targetSeconds : Real;
    attribute toleranceSeconds : Real;
  }

  requirement def UniformBrowningReq {
    doc /* Browning uniform to +/- 10% across bread face. PER-002. */;
    attribute uniformityPercent : Real;
  }

  requirement def PowerReq {
    doc /* Rated 800-900W at 120VAC 60Hz. PER-003. */;
    attribute minWatts : Real;
    attribute maxWatts : Real;
  }

  requirement def EjectionReq {
    doc /* Toast ejected minimum 15mm above slot rim. PER-004. */;
    attribute minEjectionHeight : Real;
  }

  requirement def UL1026Req {
    doc /* Product shall comply with UL 1026 for household cooking appliances. REG-001. */;
  }

  requirement def IEC60335Req {
    doc /* Product shall comply with IEC 60335-2-9 for toaster safety. REG-002. */;
  }

  requirement def FlammabilityReq {
    doc /* All plastics shall meet UL 94 V-0 flammability rating. REG-003. */;
    attribute flameRating : String;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Verification Cases
  // ══════════════════════════════════════════════════════════════════════

  verification def ThermalFuseTest {
    doc /* Verify thermal fuse trips at 240 deg C via oven ramp test. */;
    subject : ThermalSafetyReq;
  }

  verification def InterlockTest {
    doc /* Verify no element current flows with lever unlatched. */;
    subject : InterlockReq;
  }

  verification def SurfaceTempTest {
    doc /* Thermocouple measurement of all accessible surfaces under load. */;
    subject : SurfaceTempReq;
  }

  verification def ToastQualityTest {
    doc /* Controlled browning test at each dial setting with standard bread. */;
    subject : ToastTimeReq;
  }

  verification def EjectionHeightTest {
    doc /* Measure toast ejection height with high-speed camera. */;
    subject : EjectionReq;
  }

  verification def BrowningUniformityTest {
    doc /* IR camera scan of toast surface post-browning, measuring delta-E across bread face. */;
    subject : UniformBrowningReq;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Constraint Definitions
  // ══════════════════════════════════════════════════════════════════════

  constraint def PowerBudget {
    doc /* Total power draw shall not exceed 900W at 120VAC. */;
    Toaster.ratedPower <= 900
  }

  constraint def ThermalEnvelope {
    doc /* Internal air temp shall stay below 200 deg C during normal operation. */;
    internalAirTemp <= 200
  }

  // ══════════════════════════════════════════════════════════════════════
  // Analysis Definitions
  // ══════════════════════════════════════════════════════════════════════

  analysis def ThermalDistribution {
    doc /* FEA thermal analysis of heat distribution across bread chamber. */;
  }

  analysis def SpringFatigueLife {
    doc /* Fatigue analysis of carriage compression spring — target 100k cycles. */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Connection Definitions
  // ══════════════════════════════════════════════════════════════════════

  connection def ACPowerConnection {
    doc /* 120VAC mains power delivery connection. */;
  }

  connection def HeatFlowConnection {
    doc /* Thermal radiation path from elements to bread chamber. */;
  }

  connection def MechanicalLinkage {
    doc /* Force transmission between mechanical subsystems. */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Item Definitions — flow types for sequence and activity diagrams
  // ══════════════════════════════════════════════════════════════════════

  item def MechanicalForce {
    doc /* Physical force transmission between assemblies. */;
  }

  item def ElectricalSignal {
    doc /* Electrical control signal between subsystems. */;
  }

  item def ThermalFeedback {
    doc /* Temperature sensor feedback from bimetal strip. */;
  }

  item def ThermalSignal {
    doc /* Thermal event trigger from heating elements. */;
  }

  item def MechanicalInput {
    doc /* User mechanical input via lever. */;
  }

  item def ThermalDeflection {
    doc /* Bimetal strip deflection releasing latch. */;
  }

  item def HeatRadiation {
    doc /* Infrared thermal radiation from elements to bread. */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Action Definitions — behavioral specs for sequence + activity diagrams
  // ══════════════════════════════════════════════════════════════════════

  action def ToastCycle {
    doc /* Full toasting lifecycle from lever press to pop-up ejection. */;
    action pressLever;
    action engageLatch;
    action energizeElements;
    action heatBread;
    action timerTrip;
    action releaseLatch;
    action ejectToast;
    flow of MechanicalInput from pressLever to engageLatch;
    flow of ElectricalSignal from engageLatch to energizeElements;
    flow of HeatRadiation from energizeElements to heatBread;
    flow of ThermalDeflection from timerTrip to releaseLatch;
  }

  action def SafetyShutdown {
    doc /* Emergency thermal safety shutdown sequence. */;
    action detectOvertemp;
    action tripThermalFuse;
    action deEnergizeElements;
    action alertUser;
    flow of ThermalFeedback from detectOvertemp to tripThermalFuse;
    flow of ElectricalSignal from tripThermalFuse to deEnergizeElements;
    flow of ThermalSignal from deEnergizeElements to alertUser;
  }

  action def BrowningAdjustment {
    doc /* User browning level adjustment via dial. */;
    action turnDial;
    action adjustPotentiometer;
    action modifyHeaterCurrent;
    action updateTimerCalibration;
    flow of MechanicalInput from turnDial to adjustPotentiometer;
    flow of ElectricalSignal from adjustPotentiometer to modifyHeaterCurrent;
    flow of ThermalFeedback from modifyHeaterCurrent to updateTimerCalibration;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Interface Definitions — port-to-port contracts
  // ══════════════════════════════════════════════════════════════════════

  interface def ACPowerInterface {
    doc /* 120VAC mains power delivery contract between supplier and consumer ports. */;
    end supplier : ElectricalPort;
    end consumer : ElectricalPort;
  }

  interface def ThermalInterface {
    doc /* Heat transfer contract — radiation path from elements to bread chamber. */;
    end source : ThermalPort;
    end sink : ThermalPort;
  }

  interface def MechanicalInterface {
    doc /* Force transmission contract between mechanical subsystems. */;
    end driver : MechanicalPort;
    end driven : MechanicalPort;
  }

  interface def SignalInterface {
    doc /* Electrical control signal contract between control and power subsystems. */;
    end sender : SignalPort;
    end receiver : SignalPort;
  }

  // ══════════════════════════════════════════════════════════════════════
  // Verify Relationships — link verification cases to requirements
  // ══════════════════════════════════════════════════════════════════════

  verify ThermalSafetyReq by ThermalFuseTest;
  verify InterlockReq by InterlockTest;
  verify SurfaceTempReq by SurfaceTempTest;
  verify ToastTimeReq by ToastQualityTest;
  verify EjectionReq by EjectionHeightTest;
  verify UniformBrowningReq by BrowningUniformityTest;

  // ══════════════════════════════════════════════════════════════════════
  // Satisfy Relationships — requirements traceability
  // ══════════════════════════════════════════════════════════════════════

  satisfy ThermalSafetyReq by ThermalFuse;
  satisfy InterlockReq by SafetyInterlock;
  satisfy SurfaceTempReq by HeatShieldSet;
  satisfy GroundReq by ChassisGroundConnection;
  satisfy ToastTimeReq by TimerCircuit;
  satisfy UniformBrowningReq by HeatingElementBank;
  satisfy PowerReq by PowerSystem;
  satisfy EjectionReq by SpringAssembly;
  satisfy UL1026Req by Toaster;
  satisfy IEC60335Req by Toaster;
  satisfy FlammabilityReq by ChassisAssembly;

  // ══════════════════════════════════════════════════════════════════════
  // Allocations — behavior-to-structure mapping
  // ══════════════════════════════════════════════════════════════════════

  allocate ToastCycle.pressLever to LeverAssembly;
  allocate ToastCycle.engageLatch to LatchMechanism;
  allocate ToastCycle.energizeElements to HeatingElementBank;
  allocate ToastCycle.heatBread to HeatingAssembly;
  allocate ToastCycle.timerTrip to BimetallicStripAssembly;
  allocate ToastCycle.releaseLatch to LatchMechanism;
  allocate ToastCycle.ejectToast to SpringAssembly;
  allocate SafetyShutdown.detectOvertemp to BimetallicStripAssembly;
  allocate SafetyShutdown.tripThermalFuse to ThermalFuse;
  allocate SafetyShutdown.deEnergizeElements to SafetyInterlock;
  allocate SafetyShutdown.alertUser to Toaster;

  // ══════════════════════════════════════════════════════════════════════
  // Top-level Instantiation — system instance for analysis
  // ══════════════════════════════════════════════════════════════════════

  part toaster1 : Toaster;
}
`;

/** Default SysML document for first-load experience — proper SysML v2 spec
 *  with explicit relationships: composition, connection, satisfy, flows. */
export const DEFAULT_SYSML_DOCUMENT = `package VehicleModel {

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 1 — Port Definitions (Interface Contracts)
  // These define the typed interfaces between subsystems.
  // Any subsystem exposing a port commits to that contract.
  // ══════════════════════════════════════════════════════════════════════

  port def PowerPort {
    doc /* Abstract power transfer interface — mechanical or electrical. */;
    attribute powerKW : Real;
  }

  port def FuelPort :> PowerPort {
    doc /* Fuel delivery interface — specialized power port for chemical energy. */;
    attribute fuelType : String;
    attribute flowRate : Real;
  }

  port def DrivePort :> PowerPort {
    doc /* Mechanical torque transfer interface. */;
    attribute torque : Real;
    attribute rpm : Real;
  }

  port def ElectricalPort :> PowerPort {
    doc /* 12V/48V DC electrical bus interface. */;
    attribute voltage : Real;
  }

  port def ControlPort {
    doc /* Command/control signal interface — CAN, FlexRay, or Ethernet. */;
    attribute busType : String;
    attribute baudRate : Real;
  }

  port def DiagPort :> ControlPort {
    doc /* OBD-II diagnostic data interface — specializes ControlPort. */;
    attribute protocol : String;
  }

  port def ThermalPort {
    doc /* Heat transfer interface for thermal management. */;
    attribute temperatureC : Real;
    attribute heatFluxKW : Real;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 2 — Item Definitions (Flow Types)
  // These define what flows between subsystems along connections.
  // ══════════════════════════════════════════════════════════════════════

  item def Energy {
    doc /* Abstract energy flow — parent of all energy item types. */;
  }

  item def Fuel :> Energy {
    doc /* Chemical energy carrier — liquid or gaseous fuel. */;
    attribute octane : Integer;
  }

  item def ElectricalEnergy :> Energy {
    doc /* Electrical energy flow through power bus. */;
    attribute voltageV : Real;
    attribute currentA : Real;
  }

  item def Torque {
    doc /* Mechanical torque transmitted between drivetrain components. */;
    attribute newtonMeters : Real;
  }

  item def ControlSignal {
    doc /* Abstract command/control signal. */;
  }

  item def ThrottleSignal :> ControlSignal {
    doc /* Driver throttle input — 0-100% demand. */;
    attribute demandPct : Real;
  }

  item def BrakeForce :> ControlSignal {
    doc /* Brake force command — hydraulic or regenerative. */;
    attribute forceN : Real;
  }

  item def GearSelection :> ControlSignal {
    doc /* Transmission gear selection command. */;
  }

  item def DiagData :> ControlSignal {
    doc /* OBD-II diagnostic telemetry payload. */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 3 — Abstract Capability Roles (Concept Architecture)
  // These answer: "What functions must ANY vehicle perform?"
  // They are implementation-agnostic — no fuel, no gears, no pistons.
  // ══════════════════════════════════════════════════════════════════════

  part def EnergyStorage {
    doc /* Stores onboard energy in any form — chemical, electrical, kinetic. */;
    port energyOut : PowerPort;
    port controlIn : ControlPort;
  }

  part def EnergyConversion {
    doc /* Converts stored energy into mechanical work. */;
    port energyIn : PowerPort;
    port driveOut : DrivePort;
    port controlIn : ControlPort;
    port thermalOut : ThermalPort;
  }

  part def PowerDistribution {
    doc /* Transmits and distributes mechanical power to driven wheels. */;
    port driveIn : DrivePort;
    port driveOut : DrivePort;
    port controlIn : ControlPort;
  }

  part def ControlSubsystem {
    doc /* Supervisory control — coordinates all subsystem behavior. */;
    port diagOut : DiagPort;
    port electricalOut : ElectricalPort;
  }

  part def StructuralChassis {
    doc /* Provides structural integrity, occupant protection, and aerodynamics. */;
    attribute dragCoefficient : Real;
    attribute curbWeightKg : Real;
  }

  part def DecelerationSystem {
    doc /* Provides controlled vehicle deceleration — friction, regen, or both. */;
    port controlIn : ControlPort;
    port electricalIn : ElectricalPort;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 4 — Gasoline Vehicle Specialization
  // These answer: "How does a gasoline sedan implement those capabilities?"
  // Every concrete part specializes an abstract role via :>
  // ══════════════════════════════════════════════════════════════════════

  // --- Vehicle (top-level assembly) ---

  part def Vehicle {
    doc /* Self-propelled ground vehicle — abstract system-of-systems. */;
    attribute mass : Real;
    attribute maxSpeed : Real;
    port fuelIn : FuelPort;
    port diagOut : DiagPort;

    part energyStore : EnergyStorage;
    part converter : EnergyConversion;
    part drivetrain : PowerDistribution;
    part control : ControlSubsystem;
    part chassis : StructuralChassis;
    part brakes : DecelerationSystem;

    connection : energyStore -> converter;
    connection : converter -> drivetrain;
    connection : control -> converter;
    connection : control -> drivetrain;
    connection : control -> brakes;
    connection : control -> energyStore;
  }

  // --- EnergyStorage: Fuel System (gasoline specialization) ---

  part def FuelSystem :> EnergyStorage {
    doc /* Gasoline fuel delivery — tank, pump, rail, return line. */;
    port fuelIn : FuelPort;
    port fuelOut : FuelPort;
    part tank : FuelTank;
    part pump : FuelPump;
  }

  part def FuelTank {
    doc /* 60L high-density polyethylene fuel tank. */;
    attribute capacityL : Real;
  }

  part def FuelPump {
    doc /* In-tank electric fuel pump, 3.0 bar operating pressure. */;
    attribute pressureBar : Real;
  }

  // --- EnergyConversion: Internal Combustion Engine (gasoline specialization) ---

  part def ICEngine :> EnergyConversion {
    doc /* 2.0L turbocharged 4-cylinder, 250 hp, direct injection. */;
    attribute horsepower : Real;
    attribute displacementL : Real;
    port fuelIn : FuelPort;
    port electricalIn : ElectricalPort;
    part block : EngineBlock;
    part turbo : Turbocharger;
    part injectors : FuelInjectorSet;
  }

  part def EngineBlock {
    doc /* Cast aluminum cylinder block with forged steel crankshaft. */;
    attribute material : String;
    attribute cylinders : Integer;
  }

  part def Turbocharger {
    doc /* Twin-scroll turbo, 18 psi max boost, wastegate controlled. */;
    attribute maxBoostPsi : Real;
  }

  part def FuelInjectorSet {
    doc /* Direct-injection solenoid injectors, 350cc/min flow rate. */;
    attribute flowRateCcMin : Real;
    attribute quantity : Integer;
  }

  // --- PowerDistribution: Mechanical Transmission (gasoline specialization) ---

  part def MechTransmission :> PowerDistribution {
    doc /* 8-speed automatic transmission with torque converter. */;
    attribute gearCount : Integer;
    part gearbox : GearboxAssembly;
    part converter : TorqueConverter;
  }

  part def GearboxAssembly {
    doc /* Planetary gear sets with hydraulic clutch packs. */;
    attribute gearRatios : String;
  }

  part def TorqueConverter {
    doc /* Fluid coupling with lock-up clutch for efficiency. */;
    attribute stallRatio : Real;
  }

  // --- ControlSubsystem: Distributed ECU Network (gasoline specialization) ---

  part def VehicleControlUnit :> ControlSubsystem {
    doc /* Distributed controller network — not a single ECU but a subsystem. */;
    part powertrainCtrl : PowertrainController;
    part brakeCtrl : BrakeController;
    part bodyCtrl : BodyController;
    connection : powertrainCtrl -> brakeCtrl;
    connection : powertrainCtrl -> bodyCtrl;
  }

  part def PowertrainController {
    doc /* Engine and transmission control — manages fuel, ignition, shift logic. */;
    port controlOut : ControlPort;
    port diagOut : DiagPort;
  }

  part def BrakeController {
    doc /* ABS, traction control, and electronic stability program. */;
    port controlOut : ControlPort;
  }

  part def BodyController {
    doc /* Body electronics — lighting, HVAC, comfort systems. */;
    port controlOut : ControlPort;
  }

  // --- StructuralChassis: Unibody (gasoline specialization) ---

  part def UniBody :> StructuralChassis {
    doc /* Unibody steel monocoque with aluminum closures. */;
    attribute material : String;
    part frame : BodyFrame;
    part panels : BodyPanelSet;
  }

  part def BodyFrame {
    doc /* High-strength steel unibody structure, 1500 MPa. */;
    attribute material : String;
  }

  part def BodyPanelSet {
    doc /* Stamped aluminum outer panels — hood, doors, trunk. */;
    attribute material : String;
  }

  // --- DecelerationSystem: Hydraulic Brakes (gasoline specialization) ---

  part def HydraulicBrakes :> DecelerationSystem {
    doc /* 4-wheel disc brakes with ABS and electronic stability control. */;
    part frontDiscs : BrakeDisc;
    part rearDiscs : BrakeDisc;
    part absModule : ABSModule;
  }

  part def BrakeDisc {
    doc /* Ventilated cast iron rotor with semi-metallic pads. */;
    attribute diameterMm : Real;
  }

  part def ABSModule {
    doc /* 4-channel ABS with traction control integration. */;
  }

  // --- Gasoline Sedan: Concrete Vehicle Assembly ---

  part def GasolineSedan :> Vehicle {
    doc /* 2.0L turbo 4-seat sedan — concrete gasoline implementation. */;
    part energyStore : FuelSystem;
    part converter : ICEngine;
    part drivetrain : MechTransmission;
    part control : VehicleControlUnit;
    part chassis : UniBody;
    part brakes : HydraulicBrakes;

    connection : energyStore -> converter;
    connection : converter -> drivetrain;
    connection : control -> converter;
    connection : control -> drivetrain;
    connection : control -> brakes;
    connection : control -> energyStore;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 5 — Connection Definitions (Typed Links)
  // ══════════════════════════════════════════════════════════════════════

  connection def DriveConnection {
    doc /* Mechanical torque transmission between drivetrain components. */;
  }

  connection def FuelConnection {
    doc /* Pressurized fuel delivery line. */;
  }

  connection def SignalConnection {
    doc /* ECU control signal bus (CAN/FlexRay). */;
  }

  connection def ThermalConnection {
    doc /* Coolant or air-based thermal energy transfer. */;
  }

  connection def PowerBusConnection {
    doc /* Electrical power distribution (12V/48V). */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 6 — Behavior Definitions with Allocation
  // Actions describe WHAT happens. Allocations say WHO performs it.
  // ══════════════════════════════════════════════════════════════════════

  action def Accelerate {
    doc /* Driver requests speed increase — throttle to torque delivery. */;
    action requestThrottle;
    action computeFuelMap;
    action injectFuel;
    action deliverTorque;
    action shiftGear;
    flow of ThrottleSignal from requestThrottle to computeFuelMap;
    flow of Fuel from computeFuelMap to injectFuel;
    flow of Torque from injectFuel to deliverTorque;
    flow of GearSelection from deliverTorque to shiftGear;
  }

  action def Brake {
    doc /* Driver requests deceleration — pedal to wheel force. */;
    action applyPedal;
    action computeBrakeForce;
    action engageABS;
    action applyDiscs;
    flow of BrakeForce from applyPedal to computeBrakeForce;
    flow of BrakeForce from computeBrakeForce to engageABS;
    flow of BrakeForce from engageABS to applyDiscs;
  }

  action def RunDiagnostics {
    doc /* Controller self-test and diagnostic reporting cycle. */;
    action scanSensors;
    action evaluateCodes;
    action reportStatus;
    flow of DiagData from scanSensors to evaluateCodes;
    flow of DiagData from evaluateCodes to reportStatus;
  }

  // --- Behavior Allocation ---

  allocation def AccelerateAllocation {
    doc /* Accelerate behavior allocated to EnergyConversion + PowerDistribution. */;
  }

  allocation def BrakeAllocation {
    doc /* Brake behavior allocated to DecelerationSystem. */;
  }

  allocation def DiagnosticsAllocation {
    doc /* Diagnostics behavior allocated to ControlSubsystem. */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 7 — State Machine (Vehicle Lifecycle)
  // ══════════════════════════════════════════════════════════════════════

  state def VehicleStates {
    doc /* Operational states of the vehicle lifecycle. */;
    state off {
      entry RunDiagnostics;
    }
    state cranking {
      do injectFuel;
    }
    state idling {
      entry computeFuelMap;
    }
    state driving {
      do deliverTorque;
    }
    state braking {
      entry applyPedal;
      do engageABS;
    }
    transition first off then cranking;
    transition first cranking then idling;
    transition first idling then driving;
    transition first driving then braking;
    transition first braking then idling;
    transition first idling then off;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 8 — Requirements (Implementation-Agnostic)
  // Requirements are defined against capability roles, not implementations.
  // ══════════════════════════════════════════════════════════════════════

  requirement def FuelEfficiencyReq {
    doc /* The vehicle shall achieve at least 30 mpg combined city/highway. */;
    attribute targetMpg : Real;
  }

  requirement def SafetyReq {
    doc /* The vehicle shall achieve 5-star NHTSA overall safety rating. */;
  }

  requirement def PerformanceReq {
    doc /* 0-60 mph in under 6.0 seconds at curb weight. */;
    attribute targetSeconds : Real;
  }

  requirement def EmissionsReq {
    doc /* Shall meet EPA Tier 3 Bin 30 emissions standards. */;
  }

  requirement def BrakingReq {
    doc /* 60-0 mph stopping distance under 120 feet on dry pavement. */;
    attribute maxStoppingFeet : Real;
  }

  requirement def ThermalReq {
    doc /* All subsystems shall operate within thermal limits under sustained load. */;
    attribute maxTempC : Real;
  }

  // --- Satisfy: requirements trace to capability roles, not implementations ---

  satisfy FuelEfficiencyReq by EnergyConversion;
  satisfy SafetyReq by StructuralChassis;
  satisfy PerformanceReq by EnergyConversion;
  satisfy EmissionsReq by ControlSubsystem;
  satisfy BrakingReq by DecelerationSystem;
  satisfy ThermalReq by EnergyConversion;

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 9 — Constraints
  // ══════════════════════════════════════════════════════════════════════

  constraint def MaxSpeedConstraint {
    doc /* Maximum speed shall not exceed 250 km/h (electronically limited). */;
  }

  constraint def WeightConstraint {
    doc /* Curb weight shall not exceed 1800 kg for target performance. */;
  }

  constraint def ThermalEnvelopeConstraint {
    doc /* Engine bay temperature shall not exceed 150C under sustained full load. */;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 10 — Verification Cases
  // ══════════════════════════════════════════════════════════════════════

  verification def FuelEfficiencyTest {
    doc /* EPA combined cycle dynamometer test per 40 CFR Part 86. */;
    subject : FuelEfficiencyReq;
  }

  verification def CrashTest {
    doc /* NHTSA NCAP frontal, side, and rollover crash test suite. */;
    subject : SafetyReq;
  }

  verification def AccelerationTest {
    doc /* 0-60 mph acceleration test with VBOX GPS data logging. */;
    subject : PerformanceReq;
  }

  verification def EmissionsTest {
    doc /* FTP-75 and US06 drive cycle emissions measurement. */;
    subject : EmissionsReq;
  }

  verification def BrakingTest {
    doc /* 60-0 mph braking distance on ASTM E501 reference surface. */;
    subject : BrakingReq;
  }

  verification def ThermalTest {
    doc /* Sustained full-load thermal soak with IR thermography. */;
    subject : ThermalReq;
  }

  // ══════════════════════════════════════════════════════════════════════
  // LAYER 11 — Use Cases, Interfaces, Analysis
  // ══════════════════════════════════════════════════════════════════════

  use case def DriveVehicle {
    doc /* Primary use case — driver operates vehicle on public roads. */;
  }

  use case def RefuelVehicle {
    doc /* Driver refuels vehicle at a fuel station. */;
  }

  use case def ServiceVehicle {
    doc /* Technician diagnoses and services vehicle via OBD-II. */;
  }

  interface def DiagnosticsIF {
    doc /* OBD-II diagnostic interface — J1962 16-pin connector. */;
  }

  interface def PowertrainIF {
    doc /* Internal powertrain interface — EnergyConversion to PowerDistribution. */;
  }

  interface def ControlBusIF {
    doc /* Vehicle-wide CAN/FlexRay control bus shared by all controllers. */;
  }

  analysis def ThermalAnalysis {
    doc /* CFD thermal analysis of engine bay heat dissipation. */;
  }

  calc def FuelConsumption {
    doc /* Calculate instantaneous fuel consumption from MAF + injector data. */;
  }

  calc def BrakingDistance {
    doc /* Calculate stopping distance: d = v^2 / (2 * mu * g). */;
  }
}
`;
